/**
 * Library.jsx
 * Every image a brand has, uploaded or generated, newest first.
 *
 * Images are brand-scoped throughout the app — the publisher refuses to post
 * one brand's artwork to another's account — so the library is too.
 *
 * This is also where pictures arrive in bulk. Ticking several from the grid
 * turns into a carousel draft in one step, which is the only reason anybody
 * uploads twelve images at once.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import { Image as ImageIcon, Warning } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';
import ImageLightbox from '../components/ImageLightbox.jsx';
import MediaPreview, { formatDuration } from '../components/MediaPreview.jsx';
import BulkUpload from '../components/BulkUpload.jsx';

export default function Library() {
  const { brands } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [media, setMedia] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  // Images ticked in the grid, in the order they were ticked — which becomes
  // the slide order of a carousel, so it has to be a list rather than a set.
  const [picked, setPicked] = useState([]);
  // The description being typed for the picture that is open, kept apart from
  // the saved value so an unsaved edit is visibly unsaved.
  const [altDraft, setAltDraft] = useState('');
  const [savingAlt, setSavingAlt] = useState(false);

  /**
   * Saves the description.
   *
   * It matters beyond the library: this is what Facebook and Instagram are
   * given as alt text when the picture is posted, and what the caption writer
   * is told the picture contains. Left blank, Facebook invents its own.
   */
  async function saveAlt() {
    if (!selected) return;
    setSavingAlt(true);
    setError(null);
    try {
      const { media: updated } = await api.media.describe(selected.id, altDraft);
      setSelected(updated);
      setAltDraft(updated.alt_text ?? '');
      // The grid holds its own copy, and a stale one would reopen the old text.
      setMedia((list) => list.map((m) => (m.id === updated.id ? updated : m)));
      setNote('Description saved.');
    } catch (e) {
      setError(e);
    } finally {
      setSavingAlt(false);
    }
  }

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const { media: list } = await api.media.list(brandId);
      setMedia(list);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
    // Ticks belong to the brand they were made in; carrying them across would
    // mean deleting or building a carousel from something no longer on screen.
    setPicked([]);
  }, [load]);

  async function remove(item) {
    if (!confirm('Delete this image? Posts that already used it keep it in their history.')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.media.remove(item.id);
      setNote(res.message);
      setSelected(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  /**
   * Deletes everything ticked.
   *
   * The server decides what "delete" means for each one, and the two outcomes
   * are genuinely different: an image no post has used is removed from R2 and
   * the database, while one that has been published is archived — hidden from
   * the library but kept, so the Calendar can still show what actually went
   * out. Reporting a mixture as "12 deleted" would be a lie about the second
   * kind, so they are counted apart.
   *
   * Failures are collected rather than thrown, because stopping halfway
   * through twenty images leaves the worst possible state: some gone, some
   * not, and no account of which.
   */
  async function removeSelected() {
    const total = picked.length;
    if (
      !confirm(
        `Delete ${total} image${total === 1 ? '' : 's'}?\n\n` +
          'Any that a published post used will be kept on file and only hidden from the library, ' +
          'so your posting history stays intact. The rest are removed for good.',
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    setNote(null);

    let deleted = 0;
    let archived = 0;
    const failed = [];

    for (const id of picked) {
      try {
        const res = await api.media.remove(id);
        if (res.archived) archived++;
        else deleted++;
      } catch (e) {
        failed.push(e.message);
      }
    }

    setPicked([]);
    setSelected(null);
    await load();

    setNote(
      [
        deleted > 0 ? `${deleted} deleted` : null,
        archived > 0
          ? `${archived} hidden but kept, because published posts still use ${archived === 1 ? 'it' : 'them'}`
          : null,
        failed.length > 0 ? `${failed.length} could not be removed: ${failed[0]}` : null,
      ]
        .filter(Boolean)
        .join('. ') + '.',
    );

    setBusy(false);
  }

  /**
   * Turns the ticked images into a carousel draft.
   *
   * A draft, not a post: the captions still have to be written and approved
   * exactly as anything else does. This only removes the tedious part, which
   * is attaching ten images in the right order by hand.
   */
  async function makeCarousel() {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const brand = brands.find((b) => b.id === brandId);
      const connected = (brand?.accounts ?? [])
        .filter((a) => a.status === 'connected' || a.status === 'expiring')
        .map((a) => a.platform);

      // Falls back to both when nothing is connected, so a draft can still be
      // built now and published once the accounts are back.
      const platforms = connected.length ? connected : ['facebook', 'instagram'];

      await api.posts.create({
        brand_id: brandId,
        notes: `Carousel of ${picked.length} images, built from the Library.`,
        targets: platforms.map((platform) => ({
          platform,
          caption: '',
          format: 'carousel',
          media_ids: picked,
        })),
      });

      setNote(
        `Carousel draft created with ${picked.length} slides, in the order you ticked them. ` +
          'Write the captions on the Drafts screen.',
      );
      setPicked([]);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Library</div>
          <h1>Images</h1>
          <p>Everything uploaded or generated for this brand.</p>
        </div>
        <div className="page-head__actions">
          {media.length > 0 && (
            <button
              className="btn"
              onClick={() =>
                setPicked(picked.length === media.length ? [] : media.map((m) => m.id))
              }
              disabled={busy}
            >
              {picked.length === media.length ? 'Untick all' : `Tick all ${media.length}`}
            </button>
          )}
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <ImageIcon />
          <span>{note}</span>
        </div>
      )}

      {brandId && (
        <BulkUpload
          brandId={brandId}
          onDone={(added) => {
            if (added.length) {
              setNote(`${added.length} picture${added.length === 1 ? '' : 's'} added.`);
            }
            load();
          }}
        />
      )}

      {/* Ticking images is only worth anything if it leads somewhere, so this
          appears with the selection rather than sitting there empty. */}
      {picked.length > 0 && (
        <section className="card card--sticky">
          <div className="card__head">
            <div className="card__head-text">
              <h2>{picked.length} picked</h2>
              <p>
                {picked.length < 2
                  ? 'Tick another to build a carousel, or delete this one.'
                  : picked.length > 10
                    ? `Too many for a carousel, which takes ten. ${picked.length} can still be deleted together.`
                    : `They become slides 1 to ${picked.length}, in the order you ticked them.`}
              </p>
            </div>
            <div className="card__head-end">
              <button
                className="btn btn--primary"
                disabled={busy || picked.length < 2 || picked.length > 10}
                title={picked.length > 10 ? 'A carousel takes at most ten' : ''}
                onClick={makeCarousel}
              >
                Make a carousel
              </button>
              <button className="btn btn--danger" onClick={removeSelected} disabled={busy}>
                Delete {picked.length}
              </button>
              <button className="btn" onClick={() => setPicked([])} disabled={busy}>
                Clear
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="card">
        {media.length === 0 ? (
          <div className="empty">
            <div className="empty__art">
              <ImageIcon size={30} />
            </div>
            <h3>Nothing here yet</h3>
            <p>Add some above, or upload one while writing a post on the Create screen.</p>
          </div>
        ) : (
          <div className="library">
            {media.map((m) => {
              const at = picked.indexOf(m.id);
              return (
              <div className={`lib-item${at >= 0 ? ' is-picked' : ''}`} key={m.id}>
                {/* Shows the slide number rather than a plain tick, because the
                    order is the thing being chosen. */}
                <button
                  type="button"
                  className={`lib-item__pick${at >= 0 ? ' is-on' : ''}`}
                  onClick={() => toggle(m.id)}
                  aria-label={at >= 0 ? `Slide ${at + 1}, click to remove` : 'Use in a carousel'}
                  title={at >= 0 ? `Slide ${at + 1}` : 'Use in a carousel'}
                >
                  {at >= 0 ? at + 1 : ''}
                </button>

                <MediaPreview
                  media={m}
                  className="zoomable"
                  onClick={() => {
                    setSelected(m);
                    setAltDraft(m.alt_text ?? '');
                  }}
                />
                <div className="lib-item__meta">
                  <span>
                    {m.kind === 'video'
                      ? `Video ${formatDuration(m.duration_seconds)}`
                      : `${m.width}×${m.height}`}
                  </span>
                  <button
                    className="lib-item__delete"
                    onClick={() => remove(m)}
                    disabled={busy}
                    title="Delete this image"
                  >
                    Delete
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </section>

      {selected && (
        <ImageLightbox
          src={api.media.rawUrl(selected.id)}
          isVideo={selected.kind === 'video'}
          alt={selected.alt_text || ''}
          footer={
            selected.kind === 'video' ? null : (
              <>
                <textarea
                  value={altDraft}
                  onChange={(e) => setAltDraft(e.target.value)}
                  maxLength={1000}
                  placeholder="What is on this picture? Read out by screen readers, and sent to Facebook and Instagram when it is posted."
                  aria-label="Image description"
                />
                <button
                  className="btn btn--primary"
                  onClick={saveAlt}
                  disabled={savingAlt || altDraft === (selected.alt_text ?? '')}
                >
                  {altDraft === (selected.alt_text ?? '') ? 'Saved' : 'Save description'}
                </button>
              </>
            )
          }
          caption={`${selected.kind === 'video' ? 'Video' : 'Image'} · ${selected.width}×${
            selected.height
          }${selected.aspect ? ` · ${selected.aspect}` : ' · not a standard ratio'} · ${formatLondon(
            selected.created_at,
          )}`}
          onClose={() => setSelected(null)}
        />
      )}

    </>
  );
}
